import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Timer, Calculator, Check, CaretUp, CaretDown, Lightning, TrendUp, Fire, Info, ArrowClockwise, Play, ArrowRight, Notepad, ChartLineUp } from '@phosphor-icons/react';
import { WORKOUTS, getProgressiveOverload } from '../data/programData';
import { getExerciseGuide, getMuscleColor } from '../data/exerciseGuides';
import NumberStepper from '../components/NumberStepper';
import WorkoutSummary from '../components/WorkoutSummary';
import ExerciseAnimation from '../components/ExerciseAnimation';
import DynamicWarmUp from '../components/DynamicWarmUp';

export default function Workout({ user, week, day, setDay, phase, phaseInfo, dateForDay, workoutLogs, updateWorkoutLog, startRestTimer, refreshUser, showToast, customProgram }) {
  // Resolve workout from custom program or static protocol
  const getWorkout = () => {
    if (customProgram && customProgram.phases) {
      // Find current phase in custom program
      let weeksSoFar = 0;
      for (const p of customProgram.phases) {
        if (week <= weeksSoFar + (p.weeks || 12)) {
          const workouts = p.workouts || {};
          return workouts[String(day)] || null;
        }
        weeksSoFar += p.weeks || 12;
      }
      // Fallback to first phase
      const firstPhase = customProgram.phases[0];
      return firstPhase?.workouts?.[String(day)] || null;
    }
    return WORKOUTS[phase] ? WORKOUTS[phase][day] : null;
  };
  const workout = getWorkout();

  // Get max days from custom program or default 6
  const maxDays = customProgram?.phases?.[0]?.workouts
    ? Math.max(...Object.keys(customProgram.phases[0].workouts).map(Number))
    : 6;

  const [sets, setSets] = useState([]);
  const [workoutStarted, setWorkoutStarted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [expandedEx, setExpandedEx] = useState(null);
  const [showGuide, setShowGuide] = useState(null);
  const [showPlateCalc, setShowPlateCalc] = useState(false);
  const [plateTarget, setPlateTarget] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [sessionNote, setSessionNote] = useState('');
  const [showHistory, setShowHistory] = useState(null);
  const [warmUpExpanded, setWarmUpExpanded] = useState(false);
  const timerRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  // Load sets from log
  useEffect(() => {
    setSets((workoutLogs[dateForDay] || {}).sets || []);
    setSessionNote((workoutLogs[dateForDay] || {}).note || '');
  }, [dateForDay, workoutLogs]);

  // Workout timer
  useEffect(() => {
    if (workoutStarted) timerRef.current = setInterval(() => setElapsed(p => p + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [workoutStarted]);

  // Swipe between days — ignore if mostly vertical scroll
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);
  const handleTouchEnd = useCallback((e) => {
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    const dy = touchStartY.current - e.changedTouches[0].clientY;
    // Only register horizontal swipe if dx > dy and dx > threshold
    if (Math.abs(dx) > 80 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx > 0 && day < maxDays) setDay(day + 1);
      else if (dx < 0 && day > 1) setDay(day - 1);
    }
  }, [day, setDay]);

  // Rest day content (Feature 15)
  if (!workout) {
    const isRecoveryDay = day === 4 || day === 6;
    return (
      <div className="p-4 space-y-4" data-testid="workout-page">
        <div className="glass rounded-xl p-6 text-center">
          <div className="text-4xl mb-3">{isRecoveryDay ? '🧘' : '😴'}</div>
          <div className="font-head text-2xl font-bold uppercase text-white">
            {isRecoveryDay ? 'Active Recovery' : 'Rest Day'}
          </div>
          <p className="text-neutral-400 mt-2 text-sm">Recovery is where gains actually happen.</p>
        </div>
        {isRecoveryDay && (
          <div className="space-y-3">
            <div className="glass rounded-xl p-4">
              <div className="font-head text-sm font-bold uppercase text-white mb-2">Recovery Protocol</div>
              {['20 min Yoga / Mobility Flow — hip flexors, thoracic spine, QL',
                '15 min Foam Roll — IT band, QL, hip flexors, lats',
                '10 min Hip Mobility Circuit — 90/90, pigeon, banded stretches',
                '20 min Walk (Zone 1) — blood flow + recovery',
              ].map((item, i) => (
                <div key={i} className="flex gap-2 py-1.5 border-b border-border last:border-0">
                  <span className="text-brand font-mono text-xs font-bold mt-0.5">{i + 1}</span>
                  <span className="text-xs text-neutral-300 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const getSetData = (exName, setIdx) => sets.find(s => s.ex === exName && s.setIdx === setIdx) || { ex: exName, setIdx, weight: 0, reps: 0, done: false };

  const updateSet = (exName, setIdx, field, value) => {
    setSets(prev => {
      const i = prev.findIndex(s => s.ex === exName && s.setIdx === setIdx);
      if (i >= 0) { const n = [...prev]; n[i] = { ...n[i], [field]: value }; return n; }
      return [...prev, { ex: exName, setIdx, weight: 0, reps: 0, done: false, [field]: value }];
    });
  };

  // Feature 11: Different vibration patterns
  const markSetDone = (exName, setIdx, restTime) => {
    updateSet(exName, setIdx, 'done', true);
    if (navigator.vibrate) navigator.vibrate([30, 20, 30]); // short tap
    if (showToast) showToast(`${exName} — Set ${setIdx + 1}`, 'success');
    if (restTime > 0) setTimeout(() => startRestTimer(restTime), 400);
  };

  // Feature 2: Smart autofill from last session
  const autofillFromLast = () => {
    const lastSets = getPreviousSessionAllSets();
    if (lastSets.length > 0) {
      setSets(lastSets.map(s => ({ ...s, done: false })));
      if (showToast) showToast('Loaded last session weights', 'success');
    }
  };

  const copyLastWeight = (exName, setIdx) => {
    const prev = getPreviousSessionData(exName);
    if (prev.length > setIdx) {
      updateSet(exName, setIdx, 'weight', prev[setIdx].weight);
      updateSet(exName, setIdx, 'reps', prev[setIdx].reps);
    }
  };

  const saveLog = async () => {
    await updateWorkoutLog(dateForDay, sets, elapsed, sessionNote);
    if (showToast) showToast('Progress saved', 'success');
  };

  // Feature 3: Completion summary screen
  const completeWorkout = async () => {
    try { const a = new Audio('/workout-complete.mp3'); a.volume = 0.9; a.play().catch(() => {}); } catch (e) {}
    if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 100]); // Feature 11: triple buzz for complete
    await updateWorkoutLog(dateForDay, sets, elapsed, sessionNote);
    clearInterval(timerRef.current);
    setWorkoutStarted(false);
    setShowSummary(true); // Show summary screen
    try { await refreshUser(); } catch (e) {}
  };

  // Get all previous session sets (for autofill)
  const getPreviousSessionAllSets = () => {
    const sorted = Object.keys(workoutLogs).sort().reverse();
    for (const date of sorted) {
      if (date === dateForDay) continue;
      const log = workoutLogs[date];
      if (log.sets && log.sets.length > 0) return log.sets;
    }
    return [];
  };

  const getPreviousSessionData = (exName) => {
    const sorted = Object.keys(workoutLogs).sort().reverse();
    for (const date of sorted) {
      if (date === dateForDay) continue;
      const log = workoutLogs[date];
      if (log.sets) {
        const exSets = log.sets.filter(s => s.ex === exName && s.weight > 0);
        if (exSets.length > 0) return exSets;
      }
    }
    return [];
  };

  // Feature 1: Exercise history timeline
  const getExerciseHistory = (exName) => {
    const history = [];
    Object.entries(workoutLogs).forEach(([date, log]) => {
      if (log.sets) {
        const exSets = log.sets.filter(s => s.ex === exName && s.weight > 0 && s.done);
        if (exSets.length > 0) {
          const best = exSets.reduce((b, s) => s.weight > b.weight ? s : b, exSets[0]);
          history.push({ date, weight: best.weight, reps: best.reps || 0 });
        }
      }
    });
    return history.sort((a, b) => a.date.localeCompare(b.date)).slice(-8);
  };

  const getOverloadSuggestion = (exName) => {
    const exHistory = getExerciseHistory(exName);
    return getProgressiveOverload(exName, exHistory);
  };

  // Feature 6: 1RM calculator
  const calc1RM = (weight, reps) => {
    if (!weight || !reps || reps < 1) return 0;
    if (reps === 1) return weight;
    return (weight * 36 / (37 - reps)).toFixed(1);
  };

  // Feature 7: Workout duration estimate
  const estimatedDuration = workout.exercises.reduce((sum, ex) => {
    const setTime = 30; // avg 30s per set
    return sum + (ex.sets * (setTime + (ex.rest || 60)));
  }, 0);
  const estMins = Math.round(estimatedDuration / 60);

  const calcPlates = (target) => {
    let rem = (target - 20) / 2;
    const plates = [25, 20, 15, 10, 5, 2.5, 1.25];
    const result = [];
    plates.forEach(p => { const c = Math.floor(rem / p); if (c > 0) { result.push({ plate: p, count: c }); rem -= c * p; } });
    return result;
  };

  const elapsedMin = Math.floor(elapsed / 60);
  const elapsedSec = elapsed % 60;
  const completedSets = sets.filter(s => s.done).length;
  const totalSetsCount = workout.exercises.reduce((sum, ex) => sum + ex.sets, 0);
  const progressPct = totalSetsCount > 0 ? (completedSets / totalSetsCount) * 100 : 0;
  const sessionVolume = sets.reduce((sum, s) => sum + ((s.weight || 0) * (s.reps || 0)), 0);

  // Feature 3: Summary overlay
  if (showSummary) {
    return <WorkoutSummary workout={workout} sets={sets} elapsed={elapsed} phase={phase} week={week} day={day}
      user={user} workoutLogs={workoutLogs} dateForDay={dateForDay} onClose={() => setShowSummary(false)} />;
  }

  return (
    <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3" data-testid="workout-page" ref={containerRef}
      onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>

      {/* Header */}
      <div className="glass rounded-xl p-3 sm:p-4">
        <div className="flex items-center justify-between mb-1">
          <div className="min-w-0 flex-1">
            <div className="text-impact text-xl sm:text-2xl text-white truncate" data-testid="workout-title">{workout.name}</div>
            <div className="text-data text-[9px] text-neutral-500">
              Wk {week} / D{day} / P{phase} {!workoutStarted && <span className="text-neutral-600">/ ~{estMins}min</span>}
            </div>
          </div>
          {workoutStarted && (
            <div className="text-metric text-2xl sm:text-3xl text-brand flex-shrink-0 ml-2" data-testid="workout-timer">
              {elapsedMin}:{String(elapsedSec).padStart(2, '0')}
            </div>
          )}
        </div>

        {workout.focus && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <Lightning size={12} weight="bold" className="text-warning" />
            <span className="text-xs text-warning font-bold uppercase">{workout.focus}</span>
          </div>
        )}

        {workout.warmup && (
          <div className="bg-elevated/50 rounded px-2.5 py-1.5 mb-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-neutral-500">Warmup: </span>
            <span className="text-[11px] text-neutral-400">{workout.warmup}</span>
          </div>
        )}

        {/* Progress + Volume + Autofill */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <div className="flex justify-between text-[9px] mb-0.5">
              <span className="text-neutral-500 uppercase font-bold">{completedSets}/{totalSetsCount} sets</span>
              <span className="font-mono text-white">{Math.round(progressPct)}%</span>
            </div>
            <div className="h-1.5 bg-elevated rounded-sm overflow-hidden">
              <div className="h-full rounded-sm transition-all duration-500" style={{
                width: `${progressPct}%`,
                backgroundColor: progressPct >= 100 ? '#32D74B' : progressPct > 50 ? '#FFD60A' : '#FF3B30'
              }} />
            </div>
          </div>
          {sessionVolume > 0 && (
            <div className="text-right">
              <div className="font-mono text-xs font-bold text-accent">{(sessionVolume / 1000).toFixed(1)}t</div>
            </div>
          )}
          {/* Feature 2: Autofill button */}
          {!workoutStarted && completedSets === 0 && getPreviousSessionAllSets().length > 0 && (
            <button onClick={autofillFromLast}
              className="flex items-center gap-1 px-2 py-1 bg-accent/10 border border-accent/30 rounded text-[9px] text-accent font-bold uppercase active:scale-95"
              data-testid="autofill-btn">
              <ArrowClockwise size={10} weight="bold" /> Load Last
            </button>
          )}
        </div>
      </div>

      {/* Day switcher */}
      <div className="flex gap-1 sm:gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
        {Array.from({ length: maxDays }, (_, i) => i + 1).map(d => {
          const dayDate = (() => {
            if (!user.start_date) return '';
            const start = new Date(user.start_date + 'T00:00:00');
            return new Date(start.getTime() + ((week - 1) * 7 + (d - 1)) * 86400000).toISOString().split('T')[0];
          })();
          const hasDone = workoutLogs[dayDate]?.sets?.length > 0;
          return (
            <button key={d} onClick={() => setDay(d)}
              className={`flex-1 min-w-0 px-1.5 sm:px-2 py-1.5 rounded text-[10px] font-bold uppercase border transition-all active:scale-95 ${
                day === d ? 'bg-brand text-white border-brand' : hasDone ? 'bg-success/10 text-success border-success/30' : 'bg-elevated text-neutral-600 border-border'
              }`} data-testid={`workout-day-${d}`}>
              D{d}
            </button>
          );
        })}
      </div>

      {/* Dynamic Warm-Up — inline collapsible section */}
      <DynamicWarmUp
        expanded={warmUpExpanded}
        onToggle={() => setWarmUpExpanded(prev => !prev)}
        customStretches={customProgram?.stretches}
      />

      {/* Exercise list */}
      {workout.exercises.map((ex, exIdx) => {
        const isExpanded = expandedEx === exIdx;
        const exSets = Array.from({ length: ex.sets }, (_, s) => getSetData(ex.name, s));
        const exComplete = exSets.every(s => s.done);
        const exDone = exSets.filter(s => s.done).length;
        const overload = getOverloadSuggestion(ex.name);
        const guide = getExerciseGuide(ex.name);
        const prevData = getPreviousSessionData(ex.name);
        const muscleColor = getMuscleColor(ex.muscle ? ex.muscle.split(' / ')[0] : '');
        const history = showHistory === exIdx ? getExerciseHistory(ex.name) : [];

        // Feature 8: Superset detection
        const isSuperset = ex.name.includes('SS') || ex.name.includes('>');

        // Feature 12: Next exercise
        const nextEx = workout.exercises[exIdx + 1];

        return (
          <div key={exIdx}
            className={`bg-surface border rounded-md overflow-hidden transition-all ${
              exComplete ? 'border-success/50' : isExpanded ? 'border-neutral-600' : isSuperset ? 'border-accent/30' : 'border-border'
            }`} data-testid={`exercise-card-${exIdx}`}>

            {/* Feature 8: Superset label */}
            {isSuperset && !isExpanded && (
              <div className="bg-accent/10 px-3 py-0.5 text-[9px] text-accent font-bold uppercase tracking-wider">Superset</div>
            )}

            {/* Header */}
            <button onClick={() => setExpandedEx(isExpanded ? null : exIdx)}
              className="w-full p-3 flex items-center gap-2.5 active:bg-elevated/50 transition-colors"
              data-testid={`exercise-toggle-${exIdx}`}>
              {/* Animated thumbnail */}
              <ExerciseAnimation exerciseName={ex.name} />
              <div className="text-left flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: muscleColor }} />
                  <span className="font-head text-[15px] font-bold uppercase text-white leading-tight">{ex.name}</span>
                  {ex.isKey && <span className="text-[8px] bg-brand/20 text-brand px-1 py-px rounded font-bold">KEY</span>}
                  {ex.isHip && <span className="text-[8px] bg-accent/20 text-accent px-1 py-px rounded font-bold">HIP</span>}
                </div>
                <div className="text-[10px] text-neutral-500 mt-0.5">
                  {ex.sets}x{ex.reps} {ex.rest > 0 ? `/ ${ex.rest}s` : ''} / <span style={{ color: muscleColor }}>{ex.muscle}</span>
                </div>
                {!isExpanded && ex.note && <div className="text-[9px] text-neutral-600 mt-0.5 italic truncate">{ex.note}</div>}
              </div>
              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                {exDone > 0 && !exComplete && <span className="text-[9px] font-mono text-warning font-bold">{exDone}/{ex.sets}</span>}
                {exComplete && <Check size={18} weight="bold" className="text-success" />}
                {isExpanded ? <CaretUp size={14} className="text-neutral-500" /> : <CaretDown size={14} className="text-neutral-500" />}
              </div>
            </button>

            {/* Expanded */}
            {isExpanded && (
              <div className="border-t border-border p-3 space-y-3">
                {/* Note */}
                {ex.note && (
                  <div className="text-[11px] text-neutral-400 italic bg-elevated/50 rounded px-2.5 py-1.5 border-l-2" style={{ borderLeftColor: muscleColor }}>
                    {ex.note}
                  </div>
                )}

                {/* Guide + History toggles */}
                <div className="flex gap-2">
                  {guide && (
                    <button onClick={() => setShowGuide(showGuide === exIdx ? null : exIdx)}
                      className="flex items-center gap-1 text-[10px] text-accent font-bold uppercase" data-testid={`guide-toggle-${exIdx}`}>
                      <Info size={12} weight="bold" /> {showGuide === exIdx ? 'Hide' : 'How To'}
                    </button>
                  )}
                  <button onClick={() => setShowHistory(showHistory === exIdx ? null : exIdx)}
                    className="flex items-center gap-1 text-[10px] text-neutral-500 font-bold uppercase" data-testid={`history-toggle-${exIdx}`}>
                    <ChartLineUp size={12} weight="bold" /> History
                  </button>
                </div>

                {/* Feature 4: Guide */}
                {showGuide === exIdx && guide && (
                  <div className="bg-accent/5 border border-accent/20 rounded p-2.5 space-y-1.5">
                    <div className="text-[11px] text-white leading-relaxed">{guide.cue}</div>
                    <div className="flex flex-wrap gap-1">
                      {guide.targets.map((t, i) => (
                        <span key={i} className="text-[8px] px-1.5 py-px rounded-full font-bold uppercase"
                          style={{ backgroundColor: getMuscleColor(t) + '25', color: getMuscleColor(t) }}>{t}</span>
                      ))}
                    </div>
                    <div className="text-[10px] text-accent italic">{guide.tip}</div>
                  </div>
                )}

                {/* Feature 1: History mini chart */}
                {showHistory === exIdx && history.length > 0 && (
                  <div className="bg-elevated/50 border border-border rounded p-2.5">
                    <div className="text-[9px] text-neutral-500 uppercase font-bold mb-1.5">Weight Progression</div>
                    <div className="flex items-end gap-1 h-16">
                      {history.map((h, i) => {
                        const maxW = Math.max(...history.map(x => x.weight));
                        const minW = Math.min(...history.map(x => x.weight));
                        const range = maxW - minW || 1;
                        const pct = ((h.weight - minW) / range) * 80 + 20;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                            <span className="text-[8px] font-mono text-accent font-bold">{h.weight}</span>
                            <div className="w-full rounded-t" style={{ height: `${pct}%`, backgroundColor: muscleColor + '80' }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Overload suggestion */}
                {overload && (
                  <div className="flex items-center gap-1.5 p-2 bg-accent/10 border border-accent/20 rounded">
                    <TrendUp size={12} weight="bold" className="text-accent flex-shrink-0" />
                    <span className="text-[10px] text-accent font-bold">{overload.message}</span>
                  </div>
                )}

                {/* Sets */}
                {exSets.map((setData, sIdx) => {
                  const prev = prevData.length > sIdx ? prevData[sIdx] : null;
                  const est1rm = calc1RM(setData.weight, setData.reps);
                  return (
                    <div key={sIdx} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Set {sIdx + 1}</span>
                          {prev && !setData.weight && (
                            <button onClick={() => copyLastWeight(ex.name, sIdx)}
                              className="text-[9px] text-neutral-600 hover:text-accent transition-colors" data-testid={`copy-last-${exIdx}-${sIdx}`}>
                              <ArrowClockwise size={9} className="inline" /> {prev.weight}kg x{prev.reps}
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Feature 6: 1RM */}
                          {est1rm > 0 && setData.done && (
                            <span className="text-[9px] font-mono text-neutral-600">1RM: {est1rm}kg</span>
                          )}
                          {setData.done && <span className="text-[9px] text-success font-bold">DONE</span>}
                        </div>
                      </div>
                      <div className="flex items-end gap-1.5 sm:gap-2 w-full">
                        <NumberStepper label="KG" value={setData.weight} onChange={(v) => updateSet(ex.name, sIdx, 'weight', v)} step={2.5} max={500} />
                        <NumberStepper label="REPS" value={setData.reps} onChange={(v) => updateSet(ex.name, sIdx, 'reps', v)} step={1} max={100} />
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 opacity-0">OK</span>
                          <button onClick={() => markSetDone(ex.name, sIdx, ex.rest || 0)}
                            className={`h-11 w-11 sm:h-14 sm:w-14 flex items-center justify-center rounded-md border font-bold transition-all active:scale-95 flex-shrink-0 ${
                              setData.done ? 'bg-success border-success text-black' : 'bg-elevated border-border text-white'
                            }`} data-testid={`log-set-btn-${exIdx}-${sIdx}`}>
                            <Check size={18} weight="bold" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Actions */}
                <div className="flex gap-2">
                  {ex.rest > 0 && (
                    <button onClick={() => startRestTimer(ex.rest)}
                      className="flex-1 flex items-center justify-center gap-1 bg-elevated border border-border text-white font-bold uppercase text-[10px] h-9 rounded active:scale-95"
                      data-testid={`rest-btn-${exIdx}`}>
                      <Timer size={12} weight="bold" /> {ex.rest}s
                    </button>
                  )}
                  <button onClick={() => setShowPlateCalc(true)}
                    className="flex items-center justify-center gap-1 bg-elevated border border-border text-white font-bold uppercase text-[10px] h-9 px-3 rounded active:scale-95"
                    data-testid="plate-calc-btn">
                    <Calculator size={12} weight="bold" /> Plates
                  </button>
                </div>

                {/* Feature 12: Next exercise preview */}
                {nextEx && (
                  <div className="flex items-center gap-2 pt-1 border-t border-border">
                    <ArrowRight size={10} className="text-neutral-600" />
                    <span className="text-[9px] text-neutral-600 uppercase">Next:</span>
                    <span className="text-[10px] text-neutral-400 font-medium">{nextEx.name}</span>
                    <span className="text-[9px] font-mono text-neutral-600">{nextEx.sets}x{nextEx.reps}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Cardio */}
      {workout.cardio && (
        <div className="glass rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Fire size={14} weight="bold" className="text-brand" />
            <span className="font-head text-xs font-bold uppercase text-white">Cardio</span>
          </div>
          <div className="text-[11px] text-neutral-300 leading-relaxed">{workout.cardio}</div>
        </div>
      )}

      {/* Feature 14: Session notes */}
      <div className="glass rounded-xl p-3">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Notepad size={14} weight="bold" className="text-neutral-500" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Session Notes</span>
        </div>
        <textarea value={sessionNote} onChange={(e) => setSessionNote(e.target.value)}
          placeholder="How did it feel? Any pain? Energy level?"
          className="w-full bg-black/50 border border-border text-white text-xs rounded px-3 py-2 min-h-[48px] resize-none outline-none focus:border-brand"
          data-testid="session-notes" />
      </div>

      {/* Actions */}
      <div className="space-y-2 pb-4">
        {!workoutStarted ? (
          <button onClick={() => { setWorkoutStarted(true); setElapsed(0); if (showToast) showToast('Let\'s go', 'fire'); }}
            className="w-full bg-brand text-white text-command tracking-[0.15em] h-14 sm:h-16 rounded-xl active:scale-95 transition-all flex items-center justify-center gap-2 glow-subtle"
            data-testid="start-workout-btn">
            <Play size={20} weight="bold" /> START WORKOUT
          </button>
        ) : (
          <button onClick={completeWorkout}
            className={`w-full text-command tracking-[0.15em] h-14 sm:h-16 rounded-xl active:scale-95 transition-all glow-subtle ${
              progressPct >= 100 ? 'bg-success text-black' : 'bg-success/70 text-black'
            }`} data-testid="complete-workout-btn">
            {progressPct >= 100 ? 'COMPLETE WORKOUT' : `FINISH (${completedSets}/${totalSetsCount})`}
          </button>
        )}
        <button onClick={saveLog}
          className="w-full glass-elevated text-white text-command tracking-[0.1em] text-xs h-10 sm:h-11 rounded-xl active:scale-95"
          data-testid="save-workout-btn">
          SAVE PROGRESS
        </button>
      </div>

      {/* Plate Calc */}
      {showPlateCalc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(18,18,20,0.94)', backdropFilter: 'blur(20px)' }} data-testid="plate-calc-modal">
          <div className="glass rounded-xl p-5 w-full max-w-sm glow-border">
            <h3 className="font-head text-lg font-bold uppercase text-white mb-3">Plate Calculator</h3>
            <input type="number" value={plateTarget} onChange={(e) => setPlateTarget(e.target.value)}
              placeholder="Target weight (kg)"
              className="w-full bg-black/60 border border-border text-white h-14 text-lg rounded-lg px-4 font-mono mb-3 outline-none focus:border-brand"
              data-testid="plate-calc-input" />
            {plateTarget && parseFloat(plateTarget) > 20 && (
              <div className="space-y-1.5 mb-3">
                <div className="text-[9px] font-bold uppercase text-neutral-500">Each side (20kg bar):</div>
                {calcPlates(parseFloat(plateTarget)).map((p, i) => (
                  <div key={i} className="flex justify-between glass-elevated rounded-xl px-3 py-2 text-sm">
                    <span className="text-white font-bold">{p.count}x</span>
                    <span className="font-mono text-brand font-bold">{p.plate}kg</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => { setShowPlateCalc(false); setPlateTarget(''); }}
              className="w-full bg-brand text-white font-head font-bold uppercase h-11 rounded-lg active:scale-95 glow-subtle" data-testid="plate-calc-close">CLOSE</button>
          </div>
        </div>
      )}
    </div>
  );
}
